import { useNavigate } from '@tanstack/react-router';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from 'components/ui/Accordion';
import { Button } from 'components/ui/Button';
import { CardSlider } from 'components/ui/CardSlider';
import { SectionLabel } from 'components/ui/SectionLabel';
import { RouterKey } from 'consts';
import { useLanguage } from 'context/language.context';
import { StringKey } from 'dictionaries/string-key.enum';
import { cn } from 'lib/utils';
import { AnimatePresence, motion } from 'motion/react';
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';

export const FeaturesExtraSection = () => {
  const { t, direction } = useLanguage();
  const navigate = useNavigate();

  const accordionContent: {
    title: string;
    description: string[];
    children?: ReactNode;
    image?: ReactNode;
  }[] = useMemo(
    () => [
      {
        title: t(StringKey.ORGANIZER),
        description: [t(StringKey.ORGANIZER_DESC)],
        children: (
          <div className="flex flex-wrap-reverse gap-3 text-m mt-5">
            <Button
              variant="tertiary"
              className="max-xs:w-full px-5 py-3 text-sm md:px-[26px] md:py-4 md:text-m"
              onClick={() => navigate({ to: RouterKey.REQUEST_DEMO as string })}
            >
              {t(StringKey.BOOK_A_DEMO)}
            </Button>
            <Button
              className="max-xs:w-full px-5 py-3 text-sm md:px-[26px] md:py-4 md:text-m"
              onClick={() => navigate({ to: RouterKey.REGISTER_NOW as string })}
            >
              {t(StringKey.JOIN_WAITLIST)}
            </Button>
          </div>
        ),
        image: (
          <motion.div
            key={'organizer-image'}
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.5 }}
            transition={{
              type: 'tween',
              ease: 'easeOut',
              duration: 0.6,
              delay: 0,
            }}
            className={cn(
              'w-[calc(100%-16px)] lg:w-full flex justify-end',
              direction === 'rtl' ? 'max-lg:mr-4' : 'max-lg:ml-4',
            )}
          >
            <img
              src={`assets/accordion-organizer${direction === 'rtl' ? '-ar' : ''}.png`}
              alt="accordion-image"
              className={cn(
                'relative max-lg:h-[512px] lg:-top-20 lg:h-[650px] object-cover',
                direction === 'rtl' ? 'object-right' : 'object-left',
              )}
            />
          </motion.div>
        ),
      },
      {
        title: t(StringKey.VENDOR_SPONSOR_EXHIBITOR_SPEAKER),
        description: [t(StringKey.VENDOR_SPONSOR_DESC)],
        children: (
          <div className="flex flex-wrap-reverse gap-3 text-m mt-5">
            <Button
              variant="tertiary"
              className="max-xs:w-full px-5 py-3 text-sm md:px-[26px] md:py-4 md:text-m"
              onClick={() => navigate({ to: RouterKey.REQUEST_DEMO as string })}
            >
              {t(StringKey.BOOK_A_DEMO)}
            </Button>
            <Button
              className="max-xs:w-full px-5 py-3 text-sm md:px-[26px] md:py-4 md:text-m"
              onClick={() => navigate({ to: RouterKey.REGISTER_NOW as string })}
            >
              {t(StringKey.JOIN_WAITLIST)}
            </Button>
          </div>
        ),
        image: (
          <motion.div
            key={'vendor-sponsor-speaker-exhibitor-image'}
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.5 }}
            transition={{
              type: 'tween',
              ease: 'easeOut',
              duration: 0.6,
              delay: 0,
            }}
            className={cn(
              'w-[calc(100%-16px)] lg:w-full flex justify-end',
              direction === 'rtl' ? 'max-lg:mr-4' : 'max-lg:ml-4',
            )}
          >
            <img
              src={`assets/accordion-organizer${direction === 'rtl' ? '-ar' : ''}.png`}
              alt="accordion-image"
              className={cn(
                'relative max-lg:h-[512px] lg:-top-20 lg:h-[650px] object-cover',
                direction === 'rtl' ? 'object-right' : 'object-left',
              )}
            />
          </motion.div>
        ),
      },
      {
        title: t(StringKey.ATTENDEE),
        description: [t(StringKey.ATTENDEE_DESC)],
        children: (
          <div className="flex flex-wrap items-center justify-start gap-3 mt-5">
            {/* TODO: Enable app download links when available
              <>
                <a href="/">
                  <AppStoreBadge />
                </a>
                <a href="/">
                  <GooglePlayBadge />
                </a>
              </>
            */}
          </div>
        ),
        image: (
          <motion.div
            key={'attendee-image'}
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.5 }}
            transition={{
              type: 'tween',
              ease: 'easeOut',
              duration: 0.6,
              delay: 0,
            }}
            className="w-full flex justify-center"
          >
            <img
              src={`assets/accordion-attendee${direction === 'rtl' ? '-ar' : ''}.png`}
              alt="accordion-image"
              className="relative max-lg:h-[480px] lg:-top-20 lg:h-[600px] object-cover lg:object-contain"
            />
          </motion.div>
        ),
      },
    ],
    [t, direction, navigate],
  );

  const slidesContent = [
    {
      label: t(StringKey.ORGANIZER),
      description: t(StringKey.ORGANIZE_WITH_CONFIDENCE),
      content: (
        <img
          className="h-full object-cover"
          src={`assets/organization-slide${direction === 'rtl' ? '-ar' : ''}.png`}
          alt="organization-slide"
        />
      ),
    },
    {
      label: t(StringKey.VENDOR),
      description: t(StringKey.WIN_MORE_TENDERS),
      content: (
        <img
          className="h-full object-cover"
          src={`assets/stages-slide${direction === 'rtl' ? '-ar' : ''}.png`}
          alt="stages-slide"
        />
      ),
    },
    {
      label: t(StringKey.ORGANIZER),
      description: t(StringKey.BRANDED_TICKETS),
      content: (
        <img
          className="h-full object-cover"
          src={`assets/tickets-slide${direction === 'rtl' ? '-ar' : ''}.png`}
          alt="tickets-slide"
        />
      ),
    },
    {
      label: t(StringKey.SPONSOR),
      description: t(StringKey.SPONSORS_IMPACT),
      content: (
        <img
          className="h-full object-cover"
          src={`assets/contracts-slide${direction === 'rtl' ? '-ar' : ''}.png`}
          alt="contracts-slide"
        />
      ),
    },
    {
      label: t(StringKey.ORGANIZER),
      description: t(StringKey.ORGANIZE_WITH_CONFIDENCE),
      content: (
        <img
          className="h-full object-cover"
          src={`assets/organization-slide${direction === 'rtl' ? '-ar' : ''}.png`}
          alt="organization-slide"
        />
      ),
    },
  ];

  const [openedAccordion, setOpenedAccordion] = useState<number>(0);

  const parentRef = useRef<HTMLDivElement>(null);
  const [slidesShown, setSlidesShown] = useState<number>(0);

  useEffect(() => {
    const parentDiv = parentRef.current;
    if (!parentDiv) return;

    const resizeObserver = new ResizeObserver(() => {
      if (window.innerWidth >= 1280) {
        setSlidesShown(4);
      } else if (window.innerWidth >= 1024) {
        setSlidesShown(3);
      } else if (window.innerWidth >= 640) {
        setSlidesShown(2);
      } else {
        setSlidesShown(1);
      }
    });

    resizeObserver.observe(parentDiv);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <section
      ref={parentRef}
      id="featuresExtra"
      className="relative w-full overflow-x-hidden flex flex-col gap-[80px] md:gap-[150px] bg-[linear-gradient(180deg,#050407_0%,#050407_25%)] py-[80px] md:py-[150px]"
    >
      <div>
        <img
          className={cn(
            'absolute -rotate-90 -mt-[120px] md:-mt-[190px] h-[400px] w-[320px] opacity-60 aspect-square object-cover object-left',
            direction === 'rtl' ? 'scale-y-[-1] left-0' : 'right-0',
          )}
          src="/assets/laptop-bg.png"
          alt="bg-pattern-top-corner"
        />
        <div className="px-4 sm:px-16 md:px-28">
          <div className="relative lg:max-w-[80%]">
            <SectionLabel>{t(StringKey.FEATURES_SECTION_EXTRA_LABEL_1)}</SectionLabel>
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ ease: 'easeOut', duration: 0.6, delay: 0 }}
            >
              <h3 className="bg-clip-text bg-label-gradient mt-2 font-semibold text-[30px] leading-[38px] md:text-4xl text-transparent">
                {t(StringKey.FEATURES_SECTION_EXTRA_TITLE_1)}
              </h3>
              <p className="mt-3 lg:max-w-[80%]">{t(StringKey.FEATURES_SECTION_EXTRA_DESC_1)}</p>
            </motion.div>
          </div>
        </div>
        <div
          className={cn(
            'relative mt-11 md:mt-16 flex gap-8',
            direction === 'rtl'
              ? 'right-4 sm:right-16 md:right-28'
              : 'left-4 sm:left-16 md:left-28',
          )}
        >
          <CardSlider data={slidesContent} slidesShown={slidesShown || 4} />
        </div>
      </div>
      <div className="px-4 sm:px-16 md:px-28">
        <div className="relative lg:max-w-[80%]">
          <SectionLabel>{t(StringKey.FEATURES_SECTION_EXTRA_LABEL_2)}</SectionLabel>
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ ease: 'easeOut', duration: 0.6, delay: 0 }}
          >
            <h3 className="bg-clip-text bg-label-gradient mt-2 font-semibold text-[30px] leading-[38px] md:text-4xl text-transparent">
              {t(StringKey.FEATURES_SECTION_EXTRA_TITLE_2)}
            </h3>
            <p className="mt-3 lg:max-w-[80%]">{t(StringKey.FEATURES_SECTION_EXTRA_DESC_2)}</p>
          </motion.div>
        </div>
        <motion.div
          initial={{ opacity: 0, scale: 0.5 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ ease: 'easeOut', duration: 0.6, delay: 0 }}
          className="relative mt-11 md:mt-16 flex flex-col z-5 md:gap-[22px] py-2.5 md:py-8 rounded-3xl border border-[#333333] bg-[linear-gradient(144.42deg,#151515_0%,#101010_100%)]"
        >
          <img
            src="assets/pattern-gray-bg.png"
            alt="pattern-gray-bg"
            className="min-h-8 object-cover"
          />
          <div className="flex flex-col lg:flex-row gap-5 lg:gap-8">
            <div
              className={cn(
                'mx-4 md:mx-10 lg:w-[45%]',
                direction === 'rtl' ? 'lg:ml-0' : 'lg:mr-0',
              )}
            >
              <Accordion
                onValueChange={(value) => setOpenedAccordion(+value || 0)}
                type="single"
                collapsible
              >
                {accordionContent.map(({ title, description, children }, index) => (
                  <AccordionItem
                    key={index}
                    className={cn(index === accordionContent.length - 1 && 'border-b-0')}
                    value={`${index}`}
                  >
                    <AccordionTrigger>
                      <p className="text-start text-m sm:text-xl">{title}</p>
                    </AccordionTrigger>
                    <AccordionContent className={cn(direction === 'rtl' ? 'md:ml-11' : 'md:mr-11')}>
                      {description.map((text, index) => (
                        <p key={index}>{text}</p>
                      ))}
                      {children}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
            <div className="relative max-h-[450px] lg:max-w-[55%-40px] flex-1">
              <AnimatePresence mode="wait">
                {accordionContent[openedAccordion].image}
              </AnimatePresence>
            </div>
          </div>
          <img
            src="assets/pattern-gray-bg.png"
            alt="pattern-gray-bg"
            className="min-h-8 object-cover"
          />
        </motion.div>
        <img
          className={cn(
            'absolute z-0 -mt-[280px] md:-mt-[210px] h-[400px] w-[320px] opacity-60 aspect-square rotate-90 object-cover object-left',
            direction === 'rtl' ? 'scale-y-[-1] right-0' : 'left-0',
          )}
          src="/assets/laptop-bg.png"
          alt="bg-pattern-bottom-corner"
        />
      </div>
    </section>
  );
};
